import type { AssistantMessage, AssistantReasoningEffort, AssistantRunMode } from '@athwab/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const buildStoredStreamingMessageSignature = (
  message: AssistantMessage | null | undefined
) => {
  if (!message) {
    return '';
  }
  return JSON.stringify(message);
};

const isPersistableStreamingMessage = (message: AssistantMessage | null | undefined) => {
  if (!message) {
    return false;
  }
  const meta = (message.metadata ?? {}) as Record<string, unknown>;
  return meta.streaming === true;
};

const sanitizeStreamingBySession = (streamingBySession: Record<string, AssistantMessage> | undefined) =>
  Object.fromEntries(
    Object.entries(streamingBySession ?? {}).filter(([, message]) =>
      isPersistableStreamingMessage(message)
    )
  );

type AssistantUiState = {
  isOpen: boolean;
  activeSessionId: string | null;
  preferredModeBySession: Record<string, AssistantRunMode>;
  preferredReasoningBySession: Record<string, AssistantReasoningEffort>;
  streamingBySession: Record<string, AssistantMessage>;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  setActiveSession: (sessionId: string | null) => void;
  reset: () => void;
  setPreferredModeForSession: (sessionId: string, mode: AssistantRunMode) => void;
  clearPreferredModeForSession: (sessionId: string) => void;
  setPreferredReasoningForSession: (sessionId: string, effort: AssistantReasoningEffort) => void;
  clearPreferredReasoningForSession: (sessionId: string) => void;
  setStreamingMessage: (sessionId: string, message: AssistantMessage) => void;
  clearStreamingMessage: (sessionId: string) => void;
};

const createInitialState = () => ({
  isOpen: false,
  activeSessionId: null,
  preferredModeBySession: {},
  preferredReasoningBySession: {},
  streamingBySession: {},
});

export const useAssistantStore = create<AssistantUiState>()(
  persist(
    (set) => ({
      ...createInitialState(),
      setOpen: (open) => set({ isOpen: open }),
      toggle: () => set((state) => ({ isOpen: !state.isOpen })),
      setActiveSession: (activeSessionId) => set({ activeSessionId }),
      reset: () => set(createInitialState()),
      setPreferredModeForSession: (sessionId, mode) =>
        set((state) => {
          if (state.preferredModeBySession[sessionId] === mode) {
            return state;
          }
          return {
            preferredModeBySession: {
              ...state.preferredModeBySession,
              [sessionId]: mode
            }
          };
        }),
      clearPreferredModeForSession: (sessionId) =>
        set((state) => {
          if (!(sessionId in state.preferredModeBySession)) {
            return state;
          }
          const next = { ...state.preferredModeBySession };
          delete next[sessionId];
          return { preferredModeBySession: next };
        }),
      setPreferredReasoningForSession: (sessionId, effort) =>
        set((state) => {
          if (state.preferredReasoningBySession[sessionId] === effort) {
            return state;
          }
          return {
            preferredReasoningBySession: {
              ...state.preferredReasoningBySession,
              [sessionId]: effort
            }
          };
        }),
      clearPreferredReasoningForSession: (sessionId) =>
        set((state) => {
          if (!(sessionId in state.preferredReasoningBySession)) {
            return state;
          }
          const next = { ...state.preferredReasoningBySession };
          delete next[sessionId];
          return { preferredReasoningBySession: next };
        }),
      setStreamingMessage: (sessionId, message) =>
        set((state) => {
          if (!isPersistableStreamingMessage(message)) {
            if (!(sessionId in state.streamingBySession)) {
              return state;
            }
            const next = { ...state.streamingBySession };
            delete next[sessionId];
            return { streamingBySession: next };
          }
          const current = state.streamingBySession[sessionId];
          if (
            buildStoredStreamingMessageSignature(current) ===
            buildStoredStreamingMessageSignature(message)
          ) {
            return state;
          }
          return {
            streamingBySession: {
              ...state.streamingBySession,
              [sessionId]: message
            }
          };
        }),
      clearStreamingMessage: (sessionId) =>
        set((state) => {
          if (!(sessionId in state.streamingBySession)) {
            return state;
          }
          const next = { ...state.streamingBySession };
          delete next[sessionId];
          return { streamingBySession: next };
        })
    }),
    {
      name: 'assistant-ui-store',
      version: 4,
      migrate: (persistedState) => {
        const state = (persistedState ?? {}) as Partial<AssistantUiState>;
        return {
          ...createInitialState(),
          activeSessionId:
            typeof state.activeSessionId === 'string' && state.activeSessionId.trim().length > 0
              ? state.activeSessionId
              : null,
          preferredModeBySession: state.preferredModeBySession ?? {},
          preferredReasoningBySession: state.preferredReasoningBySession ?? {},
          streamingBySession: sanitizeStreamingBySession(state.streamingBySession),
        };
      },
      partialize: (state) => ({
        activeSessionId: state.activeSessionId,
        preferredModeBySession: state.preferredModeBySession,
        preferredReasoningBySession: state.preferredReasoningBySession,
        streamingBySession: sanitizeStreamingBySession(state.streamingBySession)
      })
    }
  )
);
